import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, Button,
  CircularProgress, Chip, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import VideocamIcon from '@mui/icons-material/Videocam';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { SimliClient } from 'simli-client';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

// De Simli-API-key staat NIET meer in de frontend — die zit veilig op de backend.
// We halen een session-token + ICE-servers op via /Api/avatar/simli-session.
const DEFAULT_FACE_ID = process.env.REACT_APP_SIMLI_FACE_ID || 'tmp9i8bbq7c';

const AVATARS = [
  { value: 'tmp9i8bbq7c', label: 'Default' },
  { value: 'cace3ef7-a4c4-425d-a8cf-a5358eb0c427', label: 'Tina' },
  { value: 'b9e5fba3-071a-4e35-896e-211c4d6eaa7b', label: 'Laila' },
  { value: 'd2a5c7c6-fed9-4f55-bcb3-062f7cd20103', label: 'Kate' },
  { value: '7e74d6e7-d559-4394-bd56-4923a3ab75ad', label: 'Sabour' },
  { value: '1c6aa65c-d858-4721-a4d9-bda9fde03141', label: 'Fred' },
  { value: '5fc23ea5-8175-4a82-aaaf-cdd8c88543dc', label: 'Madison' },
  { value: '804c347a-26c9-4dcf-bb49-13df4bed61e8', label: 'Mark' },
  { value: 'afdb6a3e-3939-40aa-92df-01604c23101c', label: 'Zahra' },
  { value: '9d0ba12e-ebad-4bfa-b1fb-c6c5be21abca', label: 'Teenager' },
  { value: 'dd10cb5a-d31d-4f12-b69f-6db3383c006e', label: 'Hank' },
  { value: 'c65af549-9105-442a-92a3-dc6c89e34149', label: 'DJ' },
  { value: 'f0ba4efe-7946-45de-9955-c04a04c367b9', label: 'Doctor' },
  { value: 'b1f6ad8f-ed78-430b-85ef-2ec672728104', label: 'Charlotte' },
  { value: 'c295e3a2-ed11-48d5-a1bd-ff42ac7eac73', label: 'Einstein' },
  { value: '4cce0ca0-550f-42d8-b500-834ffb35e0af', label: 'Catgirl' },
  { value: 'c7451e55-ea04-41c8-ab47-bdca3e4a03d8', label: 'Cleopatra' },
  { value: '14de6eb1-0ea6-4fde-9522-8552ce691cb6', label: 'Baby' },
  { value: '6926a39d-638b-49c5-9328-79efa034e9a4', label: 'Big Foot' },
  { value: 'c2f1d5d7-074b-405d-be4c-df52cd52166a', label: 'Nonna' },
  { value: '121cd5ae-7df7-4ea3-a389-401a9463db52', label: 'Edna' },
  { value: 'f1abe833-b44c-4650-a01c-191b9c3c43b8', label: 'Tony' },
];

const VOICES = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'nova', label: 'Nova' },
  { value: 'shimmer', label: 'Shimmer' },
];

/**
 * Downsample PCM16 audio from 24kHz to 16kHz using linear interpolation.
 * Input: ArrayBuffer of PCM16 (Int16, 24kHz mono)
 * Output: Uint8Array of PCM16 (Int16, 16kHz mono)
 */
function downsample24kTo16k(pcm24Buffer) {
  const input = new Int16Array(pcm24Buffer);
  const ratio = 24000 / 16000; // 1.5
  const outputLength = Math.floor(input.length / ratio);
  const output = new Int16Array(outputLength);

  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const srcIndexFloor = Math.floor(srcIndex);
    const srcIndexCeil = Math.min(srcIndexFloor + 1, input.length - 1);
    const frac = srcIndex - srcIndexFloor;
    output[i] = Math.round(input[srcIndexFloor] * (1 - frac) + input[srcIndexCeil] * frac);
  }

  return new Uint8Array(output.buffer);
}

export default function AvatarChat() {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const simliClientRef = useRef(null);
  const chatEndRef = useRef(null);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [voice, setVoice] = useState('alloy');
  const [faceId, setFaceId] = useState(DEFAULT_FACE_ID);
  const [aspectRatio, setAspectRatio] = useState('1/1');
  const [speaking, setSpeaking] = useState(false);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      // 1. Session-token + ICE-servers via de backend-proxy (key blijft server-side).
      const sessionConfig = {
        faceId: faceId,
        handleSilence: true,
        maxSessionLength: 3600,
        maxIdleTime: 600,
      };
      const tokenResp = await authFetch(`${BACKEND_URL}/Api/avatar/simli-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: sessionConfig }),
      });
      if (!tokenResp.ok) {
        const e = await tokenResp.json().catch(() => ({}));
        throw new Error(e.details || e.error || `HTTP ${tokenResp.status}`);
      }
      const { session_token, iceServers } = await tokenResp.json();

      // 3. Create SimliClient with session token + HTML elements
      const client = new SimliClient(
        session_token,
        videoRef.current,
        audioRef.current,
        iceServers,
      );
      simliClientRef.current = client;

      // 4. Register event handlers
      client.on('start', () => {
        console.log('[AvatarChat] Simli connected');
        setConnected(true);
        setConnecting(false);
      });

      client.on('stop', () => {
        console.log('[AvatarChat] Simli disconnected');
        setConnected(false);
        setSpeaking(false);
      });

      client.on('error', (detail) => {
        console.error('[AvatarChat] Simli error:', detail);
        setMessages(prev => [...prev, {
          role: 'system',
          text: `Avatar fout: ${detail}`,
        }]);
      });

      // 5. Connect
      await client.start();
    } catch (err) {
      console.error('[AvatarChat] Connect error:', err);
      setConnecting(false);
      // SimliClient throws raw strings, not Error objects
      const errorMsg = typeof err === 'string' ? err : (err?.message || 'Onbekende fout');
      setMessages(prev => [...prev, {
        role: 'system',
        text: `Verbindingsfout: ${errorMsg}`,
      }]);
    }
  }, [faceId]);

  const handleDisconnect = useCallback(async () => {
    if (simliClientRef.current) {
      await simliClientRef.current.stop();
      simliClientRef.current = null;
    }
    setConnected(false);
    setSpeaking(false);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || !connected || speaking) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setSpeaking(true);

    try {
      const response = await authFetch(`${BACKEND_URL}/Api/avatar/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, voice }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Onbekende fout' }));
        throw new Error(err.details || err.error || `HTTP ${response.status}`);
      }

      // Get raw PCM16 24kHz audio
      const pcm24Buffer = await response.arrayBuffer();

      // Downsample 24kHz → 16kHz
      const pcm16Data = downsample24kTo16k(pcm24Buffer);

      // Send in chunks to SimliClient (6400 bytes = 200ms at 16kHz 16-bit mono)
      const CHUNK_SIZE = 6400;
      for (let offset = 0; offset < pcm16Data.length; offset += CHUNK_SIZE) {
        const chunk = pcm16Data.slice(offset, offset + CHUNK_SIZE);
        simliClientRef.current?.sendAudioData(chunk);
      }

      setMessages(prev => [...prev, { role: 'avatar', text }]);
    } catch (err) {
      console.error('[AvatarChat] TTS error:', err);
      const errorMsg = typeof err === 'string' ? err : (err?.message || 'Onbekende fout');
      setMessages(prev => [...prev, {
        role: 'system',
        text: `Fout: ${errorMsg}`,
      }]);
    } finally {
      setSpeaking(false);
    }
  }, [input, connected, speaking, voice]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 3,
      p: 3,
      maxWidth: 1400,
      mx: 'auto',
      height: 'calc(100vh - 100px)',
      flexDirection: { xs: 'column', md: 'row' },
    }}>
      {/* Chat Panel (left) */}
      <Paper elevation={0} sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        {/* Chat Header */}
        <Box sx={{
          px: 2.5, py: 2,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <VideocamIcon sx={{ color: '#6366f1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            Avatar Chat
          </Typography>
          <Chip
            label={connected ? 'Verbonden' : 'Niet verbonden'}
            size="small"
            color={connected ? 'success' : 'default'}
            sx={{ ml: 'auto' }}
          />
        </Box>

        {/* Messages */}
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}>
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
              <VideocamIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography variant="body2">
                Verbind de avatar en typ een bericht om te beginnen.
              </Typography>
            </Box>
          )}
          {messages.map((msg, i) => (
            <Box key={i} sx={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '80%',
            }}>
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor:
                    msg.role === 'user' ? '#6366f1' :
                    msg.role === 'system' ? '#fef3c7' :
                    '#f3f4f6',
                  color: msg.role === 'user' ? '#fff' : '#1f2937',
                }}
              >
                <Typography variant="body2">{msg.text}</Typography>
              </Paper>
            </Box>
          ))}
          <div ref={chatEndRef} />
        </Box>

        {/* Input Area */}
        <Box sx={{
          p: 2,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Stem</InputLabel>
            <Select
              value={voice}
              label="Stem"
              onChange={(e) => setVoice(e.target.value)}
              disabled={speaking}
            >
              {VOICES.map(v => (
                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            placeholder={connected ? 'Typ een bericht...' : 'Verbind eerst de avatar...'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!connected || speaking}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!connected || !input.trim() || speaking}
          >
            {speaking ? <CircularProgress size={24} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Paper>

      {/* Avatar Panel (right) */}
      <Paper elevation={0} sx={{
        width: { xs: '100%', md: aspectRatio === '16/9' ? 640 : aspectRatio === '9/16' ? 320 : 480 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e5e7eb',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        {/* Video Area */}
        <Box sx={{
          position: 'relative',
          backgroundColor: '#111827',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: aspectRatio,
          width: '100%',
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: connected ? 'block' : 'none',
            }}
          />
          <audio ref={audioRef} autoPlay />

          {!connected && !connecting && (
            <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
              <VideocamIcon sx={{ fontSize: 64, mb: 1 }} />
              <Typography variant="body2">Avatar niet verbonden</Typography>
            </Box>
          )}

          {connecting && (
            <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
              <CircularProgress size={40} sx={{ color: 'rgba(255,255,255,0.7)', mb: 1 }} />
              <Typography variant="body2">Verbinden...</Typography>
            </Box>
          )}
        </Box>

        {/* Controls */}
        <Box sx={{
          p: 2,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Avatar</InputLabel>
              <Select
                value={faceId}
                label="Avatar"
                onChange={(e) => setFaceId(e.target.value)}
                disabled={connected || connecting}
              >
                {AVATARS.map(a => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Formaat</InputLabel>
              <Select
                value={aspectRatio}
                label="Formaat"
                onChange={(e) => setAspectRatio(e.target.value)}
              >
                <MenuItem value="1/1">1:1</MenuItem>
                <MenuItem value="16/9">16:9</MenuItem>
                <MenuItem value="9/16">9:16</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {!connected ? (
            <Button
              variant="contained"
              startIcon={connecting ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleConnect}
              disabled={connecting}
              sx={{
                backgroundColor: '#6366f1',
                '&:hover': { backgroundColor: '#4f46e5' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {connecting ? 'Verbinden...' : 'Start Avatar'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<StopIcon />}
              onClick={handleDisconnect}
              color="error"
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Stop Avatar
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
